import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from './Button.tsx';
import { Input } from './Input.tsx';
import { useOverlayDismissGuard } from './nestedLayerGuard.ts';
import { cx } from './cx.ts';

/**
 * Radix-backed dialog with the Dispatch Deck overlay chrome: dimmed
 * backdrop, raised panel, 200ms enter motion (DESIGN.md §7). Focus
 * trapping, Escape, and aria wiring come from Radix.
 */
export type DialogSize = 'sm' | 'md' | 'lg' | 'xl';

const SIZE_CLASSES: Record<DialogSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

interface DialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly children?: ReactNode;
  readonly footer?: ReactNode;
  readonly size?: DialogSize;
  /** Accessible name for the close button — pass a translated string. */
  readonly closeLabel: string;
  readonly testId?: string;
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
  closeLabel,
  testId,
}: DialogProps): JSX.Element {
  // Block self-dismiss when the outside interaction belongs to a nested Radix
  // popper (e.g. a `Select` opened inside this dialog). See nestedLayerGuard.
  const { onInteractOutside } = useOverlayDismissGuard(open);
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 animate-overlay-in" />
        <DialogPrimitive.Content
          data-testid={testId}
          onInteractOutside={onInteractOutside}
          className={cx(
            'fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl border border-line bg-raised shadow-overlay animate-panel-in focus:outline-none',
            SIZE_CLASSES[size],
          )}
        >
          <div className="flex items-start gap-3 border-b border-line-soft px-5 py-4">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <DialogPrimitive.Title className="m-0 text-title text-paper">{title}</DialogPrimitive.Title>
              {description !== undefined && description !== null ? (
                <DialogPrimitive.Description className="m-0 text-dense text-paper-mid">
                  {description}
                </DialogPrimitive.Description>
              ) : null}
            </div>
            <DialogPrimitive.Close
              aria-label={closeLabel}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-paper-low transition-colors duration-120 hover:bg-active hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
            >
              <X aria-hidden="true" className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>
          {children !== undefined && children !== null ? (
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          ) : null}
          {footer !== undefined && footer !== null ? (
            <div className="flex items-center justify-end gap-2 border-t border-line-soft px-5 py-3.5">{footer}</div>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/**
 * Confirmation dialog for destructive / high-stakes actions. With
 * `typeToConfirm` set, the confirm button stays disabled until the
 * operator types the exact string — preserves the console's existing
 * type-to-confirm pattern on top of the new chrome.
 */
interface ConfirmDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: ReactNode;
  readonly body?: ReactNode;
  readonly confirmLabel: string;
  /** Pass translated strings — the library ships no copy of its own. */
  readonly cancelLabel: string;
  readonly closeLabel: string;
  readonly onConfirm: () => void;
  readonly tone?: 'default' | 'danger';
  readonly typeToConfirm?: string;
  readonly typeToConfirmLabel?: ReactNode;
  readonly loading?: boolean;
  readonly testId?: string;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel,
  cancelLabel,
  closeLabel,
  onConfirm,
  tone = 'default',
  typeToConfirm,
  typeToConfirmLabel,
  loading = false,
  testId,
}: ConfirmDialogProps): JSX.Element {
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (!open) {
      setTyped('');
    }
  }, [open]);

  const needsTyping = typeof typeToConfirm === 'string' && typeToConfirm.length > 0;
  const confirmDisabled = loading || (needsTyping && typed !== typeToConfirm);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      size="sm"
      closeLabel={closeLabel}
      testId={testId}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            disabled={confirmDisabled}
            loading={loading}
            data-testid={testId !== undefined ? `${testId}-confirm` : undefined}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {body !== undefined && body !== null ? <div className="text-body text-paper-mid">{body}</div> : null}
        {needsTyping ? (
          <div className="flex flex-col gap-1.5">
            {typeToConfirmLabel !== undefined && typeToConfirmLabel !== null ? (
              <p className="m-0 text-label text-paper-mid">{typeToConfirmLabel}</p>
            ) : null}
            <p className="m-0 select-all rounded bg-well px-2.5 py-1.5 font-mono text-dense text-paper">
              {typeToConfirm}
            </p>
            <Input
              mono
              value={typed}
              onChange={(event) => setTyped(event.currentTarget.value)}
              data-testid={testId !== undefined ? `${testId}-type-input` : undefined}
            />
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
